import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { CheckIcon } from '@ui/icons/Check.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { GlobeIcon } from '@ui/icons/Globe.icon'

import { useWindowResize } from '@utils/domUtils'

import { MenuSection } from '../../menuSections/useMenuSections'

import styles from './TopBar.module.scss'

interface LanguagePickerProps {
  languageMenu: MenuSection
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({ languageMenu }) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isMobile = useWindowResize()

  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])
  const close = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    if (isMobile) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isMobile])

  const items = languageMenu.screenChildren ?? []

  return (
    <div ref={containerRef} className={styles.profileContainer}>
      <IconButton.Ghost
        icon={GlobeIcon}
        size='md'
        onClick={toggle}
        aria-label={languageMenu.label}
        aria-expanded={isOpen}
      />

      {!isMobile && isOpen && (
        <div className={styles.profileDropdown}>
          {items.map((item) => (
            <button
              key={item.id}
              type='button'
              className={styles.profileDropdownItem}
              onClick={() => {
                item.onClick?.()
                setIsOpen(false)
              }}
            >
              <Body2>{item.label}</Body2>
              {item.isSelected && <CheckIcon size={16} color='currentColor' />}
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {isMobile && isOpen && (
          <MobileLanguageOverlay items={items} onClose={close} label={languageMenu.label} />
        )}
      </AnimatePresence>
    </div>
  )
}

const BackChevron = ChevronIcon.Left

interface MobileLanguageOverlayProps {
  items: MenuSection[]
  onClose: () => void
  label: string
}

const MobileLanguageOverlay: React.FC<MobileLanguageOverlayProps> = ({ items, onClose, label }) => {
  return (
    <motion.div
      className={styles.languageOverlay}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.25 }}
    >
      <button type='button' className={styles.languageOverlayBack} onClick={onClose}>
        <BackChevron size={20} color='currentColor' />
        <Body2>{label}</Body2>
      </button>

      <div className={styles.languageOverlayList}>
        {items.map((item) => (
          <button
            key={item.id}
            type='button'
            className={styles.languageOverlayItem}
            onClick={() => {
              item.onClick?.()
              onClose()
            }}
          >
            <Body2>{item.label}</Body2>
            {item.isSelected && <CheckIcon size={16} color='currentColor' />}
          </button>
        ))}
      </div>
    </motion.div>
  )
}
